import React, { useEffect, useRef, useState } from 'react';
import { X, Heart } from 'lucide-react';
import { api } from '../lib/api';
import useHomepageVisibility from '../hooks/useHomepageVisibility';

const DISMISSED_KEY = 'jdv_follower_ticker_dismissed_v1';
const FALLBACK_AVATAR =
  'https://static-cdn.jtvnw.net/user-default-pictures-uv/75305d54-c7cc-40d1-bb9c-91fbe85943c7-profile_image-300x300.png';
const REFRESH_MS = 90_000; // 1m30s

export default function FollowerTicker() {
  const { follower_ticker_duration_s: durationS } = useHomepageVisibility();
  const [followers, setFollowers] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return typeof window !== 'undefined' && sessionStorage.getItem(DISMISSED_KEY) === '1';
    } catch (e) {
      return false;
    }
  });
  const timerRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (dismissed) return undefined;
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get('/twitch/recent-followers?limit=10');
        if (!alive) return;
        const items = Array.isArray(data?.followers) ? data.followers : [];
        setFollowers(items);
      } catch (e) {
        // Non-critical — ticker stays hidden if API fails completely
      }
    };
    load();
    timerRef.current = setInterval(load, REFRESH_MS);
    return () => {
      alive = false;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [dismissed]);

  // Publish the real rendered height ONCE so other bottom-fixed elements
  // (eg. StatsTicker) can sit flush on top without a visual gap. We
  // intentionally skip ResizeObserver here: the marquee changes width on
  // every animation frame and reactive measurements interrupt the CSS
  // scroll animation.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    const el = wrapperRef.current;
    if (!el) return undefined;
    let raf = 0;
    const measure = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      if (h > 0) root.style.setProperty('--follower-ticker-h', `${h}px`);
    };
    // One initial measurement after the browser has painted.
    raf = requestAnimationFrame(measure);
    // Re-measure on viewport resize only (font/breakpoint shifts).
    window.addEventListener('resize', measure);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      root.style.setProperty('--follower-ticker-h', '0px');
    };
  }, [followers.length, dismissed]);

  if (dismissed || followers.length === 0) return null;

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(DISMISSED_KEY, '1');
    } catch (e) {
      /* ignore */
    }
    if (typeof document !== 'undefined') {
      document.documentElement.style.setProperty('--follower-ticker-h', '0px');
    }
    setDismissed(true);
  };

  // Duplicate the list once so the CSS marquee can loop seamlessly.
  // Duplicate four times so the viewport always falls within the track
  // (each copy can be narrower than 1900px on desktop, so two copies
  // aren't enough — the -50% loop would wrap mid-viewport and show a
  // visible "jump"). With four copies + a -25% scroll the seam aligns
  // to a copy boundary and the content is identical → seamless.
  const loop = [...followers, ...followers, ...followers, ...followers];

  return (
    <div
      ref={wrapperRef}
      className="follower-ticker fixed bottom-0 inset-x-0 z-[60] pointer-events-none"
      data-testid="follower-ticker"
    >
      <div className="pointer-events-auto border-t border-fuchsia-500/30 bg-gradient-to-r from-zinc-950 via-zinc-900 to-zinc-950 shadow-[0_-8px_24px_rgba(168,85,247,0.15)]">
        <div className="max-w-7xl mx-auto flex items-center gap-3 pl-3 pr-3 sm:pl-5 sm:pr-3 py-1">
          {/* Label — matches StatsTicker visual rhythm */}
          <div className="hidden sm:flex shrink-0 items-center gap-2 pr-3 border-r border-white/10">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-fuchsia-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-fuchsia-400" />
            </span>
            <Heart className="w-3.5 h-3.5 text-fuchsia-200" />
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-fuchsia-100">
              Nieuwste volgers
            </span>
          </div>

          {/* Marquee */}
          <div
            className="follower-ticker__viewport flex-1 overflow-hidden relative"
            data-testid="follower-ticker-marquee"
          >
            <div
              className="follower-ticker__track flex items-center whitespace-nowrap"
              style={{ animationDuration: `${durationS}s` }}
            >
              {loop.map((f, i) => (
                <a
                  key={`${f.user_id}-${i}`}
                  href={`https://www.twitch.tv/${f.user_login}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm group mr-6 shrink-0"
                  data-testid={i < followers.length ? `follower-ticker-item-${i}` : undefined}
                >
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/5 ring-1 ring-fuchsia-400/30 overflow-hidden">
                    <img
                      src={f.profile_image_url || FALLBACK_AVATAR}
                      onError={(e) => {
                        if (e.currentTarget.src !== FALLBACK_AVATAR) {
                          e.currentTarget.src = FALLBACK_AVATAR;
                        }
                      }}
                      alt=""
                      className="w-6 h-6 object-cover"
                    />
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-fuchsia-200/70">
                    Volger
                  </span>
                  <span className="font-bold text-pink-200 group-hover:text-white transition-colors">
                    {f.user_name}
                  </span>
                </a>
              ))}
            </div>
          </div>

          {/* Close */}
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Verberg volgers-banner"
            data-testid="follower-ticker-close"
            className="shrink-0 w-5 h-5 flex items-center justify-center rounded-full ring-1 ring-red-500/60 text-red-400 hover:text-white hover:bg-red-500/20 hover:ring-red-400 transition-colors"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
